// src/utils/featureAccess.js - With updated tier terminology
import { USER_TIERS } from '../services/UserService';

// User tier-based limits
const TIER_LIMITS = {
  [USER_TIERS.NONE]: {
    SAVED_WORKOUTS_LIMIT: 0,
    FAVORITES_LIMIT: 0,
    HISTORY_LIMIT: 0,
    PROFILES_LIMIT: 0,
    ACCESSIBLE_EXERCISES: 'all'  // 'all' or a number
  },
  [USER_TIERS.BASIC]: {
    SAVED_WORKOUTS_LIMIT: 5,
    FAVORITES_LIMIT: 10,
    HISTORY_LIMIT: 15,
    PROFILES_LIMIT: 1,
    ACCESSIBLE_EXERCISES: 'all'
  },
  [USER_TIERS.PRO]: {
    SAVED_WORKOUTS_LIMIT: Infinity,
    FAVORITES_LIMIT: Infinity,
    HISTORY_LIMIT: Infinity,
    PROFILES_LIMIT: Infinity,
    ACCESSIBLE_EXERCISES: 'all'
  }
};

// Premium-only features
const PRO_ONLY_FEATURES = [
  'detailedAnalytics',
  'workoutPrograms',
  'exportPDF',
  'customModifications',
  'advancedFilters',
  'unlimitedProfiles'
];

/**
 * Feature access utility to centralize feature availability decisions
 */
const featureAccess = {
  /**
   * Check if user has access to a feature with optional quantity limits
   * @param {string} feature - The feature to check
   * @param {number} currentCount - Current quantity (for limited features)
   * @param {string} userTier - User's tier level (MUST be passed in from component)
   * @returns {Object} Access details { hasAccess, limitReached, limit, proRequired, upgradeNeeded }
   */
  checkAccess(feature, currentCount = 0, userTier = USER_TIERS.NONE) {
    // Instead of trying to use useAuth, require userTier to be passed in
    // Default to NONE tier if not provided
    const tier = userTier || USER_TIERS.NONE;
    
    // Backward compatibility - map 'premium' to PRO
    const adjustedTier = tier === 'premium' ? USER_TIERS.PRO : tier;
    
    // Quick check for pro-only features
    if (PRO_ONLY_FEATURES.includes(feature)) {
      return {
        hasAccess: adjustedTier === USER_TIERS.PRO,
        limitReached: false,
        proRequired: true,
        upgradeNeeded: adjustedTier !== USER_TIERS.PRO
      };
    }
    
    // Determine which tier's limits to use
    const limits = TIER_LIMITS[adjustedTier] || TIER_LIMITS[USER_TIERS.NONE];
    
    switch (feature) {
      case 'savedWorkouts':
        return {
          hasAccess: adjustedTier !== USER_TIERS.NONE,
          limitReached: adjustedTier !== USER_TIERS.PRO && 
                       currentCount >= limits.SAVED_WORKOUTS_LIMIT,
          limit: adjustedTier === USER_TIERS.PRO ? null : limits.SAVED_WORKOUTS_LIMIT,
          proRequired: false,
          upgradeNeeded: adjustedTier === USER_TIERS.NONE || 
                        (adjustedTier === USER_TIERS.BASIC && 
                         currentCount >= limits.SAVED_WORKOUTS_LIMIT)
        };
      
      case 'favorites':
        return { 
          hasAccess: adjustedTier !== USER_TIERS.NONE,
          limitReached: adjustedTier !== USER_TIERS.PRO && 
                       currentCount >= limits.FAVORITES_LIMIT,
          limit: adjustedTier === USER_TIERS.PRO ? null : limits.FAVORITES_LIMIT,
          proRequired: false,
          upgradeNeeded: adjustedTier === USER_TIERS.NONE || 
                        (adjustedTier === USER_TIERS.BASIC && 
                         currentCount >= limits.FAVORITES_LIMIT)
        };
      
      case 'workoutHistory':
        return {
          hasAccess: adjustedTier !== USER_TIERS.NONE,
          limitReached: adjustedTier !== USER_TIERS.PRO && 
                       currentCount >= limits.HISTORY_LIMIT,
          limit: adjustedTier === USER_TIERS.PRO ? null : limits.HISTORY_LIMIT,
          proRequired: false,
          upgradeNeeded: adjustedTier === USER_TIERS.NONE
        };
        
      case 'detailedAnalytics':
        return {
          hasAccess: adjustedTier === USER_TIERS.PRO,
          limitReached: false,
          proRequired: true,
          upgradeNeeded: adjustedTier !== USER_TIERS.PRO
        };
        
      case 'userProfiles':
        return {
          hasAccess: adjustedTier !== USER_TIERS.NONE,
          limitReached: adjustedTier !== USER_TIERS.PRO && 
                       currentCount >= limits.PROFILES_LIMIT,
          limit: adjustedTier === USER_TIERS.PRO ? null : limits.PROFILES_LIMIT,
          proRequired: false,
          upgradeNeeded: adjustedTier === USER_TIERS.NONE || 
                        (adjustedTier === USER_TIERS.BASIC && 
                         currentCount >= limits.PROFILES_LIMIT)
        };
        
      case 'workoutPrograms':
        return {
          hasAccess: adjustedTier === USER_TIERS.PRO,
          limitReached: false,
          proRequired: true,
          upgradeNeeded: adjustedTier !== USER_TIERS.PRO
        };
        
      case 'exerciseLibrary':
        return {
          hasAccess: true, // All tiers have access
          limitReached: false,
          proRequired: false,
          upgradeNeeded: false
        };
        
      case 'workoutGenerator':
        return {
          hasAccess: true, // All tiers have access
          limitReached: false,
          proRequired: false,
          upgradeNeeded: false
        };
        
      case 'exportPDF':
        return {
          hasAccess: adjustedTier === USER_TIERS.PRO,
          limitReached: false,
          proRequired: true,
          upgradeNeeded: adjustedTier !== USER_TIERS.PRO
        };
        
      case 'customModifications':
        return {
          hasAccess: adjustedTier === USER_TIERS.PRO,
          limitReached: false,
          proRequired: true,
          upgradeNeeded: adjustedTier !== USER_TIERS.PRO
        };
        
      case 'advancedFilters':
        return {
          hasAccess: adjustedTier === USER_TIERS.PRO,
          limitReached: false,
          proRequired: true,
          upgradeNeeded: adjustedTier !== USER_TIERS.PRO
        };
        
      case 'unlimitedProfiles':
        return {
          hasAccess: adjustedTier === USER_TIERS.PRO,
          limitReached: false,
          proRequired: true,
          upgradeNeeded: adjustedTier !== USER_TIERS.PRO
        };
        
      default:
        return {
          hasAccess: true,
          limitReached: false,
          proRequired: false,
          upgradeNeeded: false
        };
    }
  },
  
  /**
   * Get limits for a feature based on user tier
   * @param {string} feature - The feature to get limits for
   * @param {string} userTier - User's tier level
   * @returns {Object|null} Limit details or null if no limits
   */
  getLimits(feature, userTier = USER_TIERS.NONE) {
    // Backward compatibility - map 'premium' to PRO
    const adjustedTier = userTier === 'premium' ? USER_TIERS.PRO : userTier;
    
    if (adjustedTier === USER_TIERS.PRO) return null; // No limits for pro users
    
    const limits = TIER_LIMITS[adjustedTier] || TIER_LIMITS[USER_TIERS.NONE];
    
    switch (feature) {
      case 'savedWorkouts':
        return { limit: limits.SAVED_WORKOUTS_LIMIT };
      case 'favorites':
        return { limit: limits.FAVORITES_LIMIT };
      case 'workoutHistory':
        return { limit: limits.HISTORY_LIMIT };
      case 'userProfiles':
        return { limit: limits.PROFILES_LIMIT };
      default:
        return null;
    }
  },
  
  /**
   * Get pro feature details for marketing purposes
   * @param {string} feature - The feature to get details for
   * @returns {Object} Feature details for marketing
   */
  getProFeatureDetails(feature) {
    switch (feature) {
      case 'savedWorkouts':
        return {
          name: 'Unlimited Saved Workouts',
          description: 'Save as many workouts as you want',
          icon: '💾',
          basicLimit: TIER_LIMITS[USER_TIERS.BASIC].SAVED_WORKOUTS_LIMIT
        };
      case 'favorites':
        return {
          name: 'Unlimited Favorites',
          description: 'Save all your favorite exercises',
          icon: '❤️',
          basicLimit: TIER_LIMITS[USER_TIERS.BASIC].FAVORITES_LIMIT
        };
      case 'detailedAnalytics':
        return {
          name: 'Detailed Analytics',
          description: 'Get comprehensive insights about your workout patterns',
          icon: '📊'
        };
      case 'workoutPrograms':
        return {
          name: 'Structured Programs',
          description: 'Access multi-week guided workout programs',
          icon: '🏆'
        };
      case 'userProfiles':
        return {
          name: 'Unlimited Profiles',
          description: 'Create profiles for different needs or family members',
          icon: '👥',
          basicLimit: TIER_LIMITS[USER_TIERS.BASIC].PROFILES_LIMIT
        };
      case 'exportPDF':
        return {
          name: 'PDF Export',
          description: 'Export your workouts as professional PDF documents',
          icon: '📄'
        };
      case 'customModifications':
        return {
          name: 'Custom Modifications',
          description: 'Create personalized modifications for any exercise',
          icon: '✏️'
        };
      case 'advancedFilters':
        return {
          name: 'Advanced Filters',
          description: 'Access detailed sorting and filtering options',
          icon: '🔍'
        };
      default:
        return {
          name: 'Premium Feature',
          description: 'Unlock this with a premium subscription',
          icon: '⭐'
        };
    }
  },
  
  /**
   * Get a list of features available for each tier
   * @returns {Object} Features by tier
   */
  getTierFeatures() {
    return {
      [USER_TIERS.NONE]: [
        'Create custom workouts',
        'Access basic exercise library',
        'Use workout generator',
        'View as guest (no saving)'
      ],
      [USER_TIERS.BASIC]: [
        'All Guest features',
        `Save up to ${TIER_LIMITS[USER_TIERS.BASIC].SAVED_WORKOUTS_LIMIT} workouts`,
        `Save up to ${TIER_LIMITS[USER_TIERS.BASIC].FAVORITES_LIMIT} favorite exercises`,
        `Create ${TIER_LIMITS[USER_TIERS.BASIC].PROFILES_LIMIT} user profile`,
        'Track workout history',
        'Basic exercise modifications'
      ],
      [USER_TIERS.PRO]: [
        'All Basic features',
        'Unlimited saved workouts',
        'Unlimited favorite exercises',
        'Unlimited user profiles',
        'Detailed workout analytics',
        'Export workouts as PDF',
        'Custom exercise modifications',
        'Advanced filtering options',
        'Structured workout programs',
        'Priority customer support'
      ]
    };
  },
  
  /**
   * Check if a feature requires a subscription upgrade
   * @param {string} feature - Feature to check
   * @param {string} userTier - Current user tier
   * @returns {boolean} Whether upgrade is needed
   */
  requiresUpgrade(feature, userTier) {
    return this.checkAccess(feature, 0, userTier).upgradeNeeded;
  }
};

export default featureAccess;